/**
 * Report Generator - FR-075 (High-Level) and FR-076 (Detailed)
 * Generates self-contained, branded HTML reports downloadable as files.
 */

export function generateReport(level, data, currentUser) {
  const { kpis, alerts, recentRequests, expiringAgreements, casesByStage, complianceScore, allRequests, ittRecords, signatureLog } = data;
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
  const timeStr = today.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });
  const isDetailed = level === 'detailed';
  const titleLabel = isDetailed ? 'Detailed Report' : 'High-Level Report';
  const preparer = currentUser ? `${currentUser.name} (${currentUser.role?.replace(/_/g,' ')})` : 'Legal Operations';

  let html = '';

  // Header
  html += `<div class="hdr">`;
  html += `<div class="hdr-logo"><span class="logo-icon">DEL</span> Desicon Engineering Limited</div>`;
  html += `<div class="hdr-sub">Legal Service Desk · ${titleLabel}</div>`;
  html += `<div class="hdr-meta">Generated ${dateStr} at ${timeStr} · Prepared by: ${preparer}</div>`;
  html += `</div>`;

  // KPIs
  html += `<h2>Executive Summary</h2>`;
  html += `<div class="kpi-grid">`;
  const kpiItems = [
    { num: kpis?.total_requests_ytd || 0, label: `Total Requests (${today.getFullYear()})`, delta: `${kpis?.requests_this_month||0} this month` },
    { num: kpis?.completed_requests_ytd || 0, label: 'Completed Requests', delta: `${kpis?.total_requests_ytd ? Math.round((kpis.completed_requests_ytd/kpis.total_requests_ytd)*100) : 0}% completion` },
    { num: kpis?.expiring_agreements || 0, label: 'Agreements Expiring (90 days)', delta: '' },
    { num: kpis?.active_cases || 0, label: 'Active Legal Cases', delta: `${kpis?.overdue_requests||0} overdue requests` },
  ];
  kpiItems.forEach(k => {
    html += `<div class="kpi"><div class="kpi-num">${k.num}</div><div class="kpi-lbl">${k.label}</div>${k.delta?`<div class="kpi-dlt">${k.delta}</div>`:''}</div>`;
  });
  html += `</div>`;

  // Alerts
  const alertsList = [];
  if (alerts?.expiringAgreements > 0) alertsList.push(`${alerts.expiringAgreements} agreement(s) expire within 30 days.`);
  if (alerts?.pendingReviews > 0) alertsList.push(`${alerts.pendingReviews} review request(s) pending over 5 business days.`);
  if (alerts?.overdueRequests > 0) alertsList.push(`${alerts.overdueRequests} request(s) have exceeded their SLA deadline.`);
  if (alertsList.length) {
    html += `<h2>Active Alerts</h2><ul class="alerts">`;
    alertsList.forEach(a => { html += `<li>${a}</li>`; });
    html += `</ul>`;
  }

  // Detailed sections
  if (isDetailed) {
    // Recent Requests
    if (recentRequests?.length) {
      html += `<h2>Recent Service Requests</h2>`;
      html += `<table><thead><tr><th>Ref</th><th>Subject</th><th>Type</th><th>Requester</th><th>Priority</th><th>Status</th></tr></thead><tbody>`;
      recentRequests.forEach(r => {
        html += `<tr><td><code>${r.ref}</code></td><td>${r.subject}</td><td>${r.type?.replace(/_/g,' ')||''}</td><td>${r.requester_name||''}</td><td>${r.priority||''}</td><td>${r.status?.replace(/_/g,' ')||''}</td></tr>`;
      });
      html += `</tbody></table>`;
    }

    // Expiring Agreements
    if (expiringAgreements?.length) {
      html += `<h2>Agreements Expiring Soon</h2>`;
      html += `<table><thead><tr><th>Agreement</th><th>Counterparty</th><th style="text-align:right">Days to Expiry</th></tr></thead><tbody>`;
      expiringAgreements.forEach(a => {
        html += `<tr><td>${a.title}</td><td>${a.counterparty||'-'}</td><td style="text-align:right;font-weight:600;color:${a.days_remaining<=30?'#DC2626':a.days_remaining<=60?'#D97706':'#059669'}">${a.days_remaining} days</td></tr>`;
      });
      html += `</tbody></table>`;
    }

    // Cases by Stage
    if (casesByStage?.length) {
      html += `<h2>Cases by Stage</h2>`;
      html += `<table><thead><tr><th>Stage</th><th style="text-align:right">Count</th></tr></thead><tbody>`;
      casesByStage.forEach(c => {
        html += `<tr><td>${c.stage?.replace(/_/g,' ')}</td><td style="text-align:right">${c.count}</td></tr>`;
      });
      html += `</tbody></table>`;
    }

    // Compliance Score
    if (complianceScore?.overall) {
      html += `<h2>Compliance Score</h2>`;
      html += `<div class="compliance-score">Overall: <strong>${complianceScore.overall.overall_score || 0}%</strong> (${complianceScore.overall.compliant||0} of ${complianceScore.overall.total||0} obligations compliant)</div>`;
      if (complianceScore.byCategory?.length) {
        html += `<table><thead><tr><th>Category</th><th style="text-align:right">Score</th><th style="text-align:right">Compliant</th><th style="text-align:right">Total</th></tr></thead><tbody>`;
        complianceScore.byCategory.forEach(c => {
          html += `<tr><td>${c.category?.replace(/_/g,' ')}</td><td style="text-align:right">${c.score_pct||0}%</td><td style="text-align:right">${c.compliant_count}</td><td style="text-align:right">${c.total}</td></tr>`;
        });
        html += `</tbody></table>`;
      }
    }

    // ITT Pipeline (FR-076)
    if (ittRecords?.length) {
      html += `<h2>ITT Pipeline</h2>`;
      html += `<table><thead><tr><th>Ref</th><th>Title</th><th>Authority</th><th style="text-align:right">Value</th><th>Category</th><th>Status</th><th>Deadline</th></tr></thead><tbody>`;
      ittRecords.forEach(r => {
        html += `<tr><td><code>${r.ref||''}</code></td><td>${r.title||''}</td><td>${r.issuing_authority||'-'}</td><td style="text-align:right">${r.contract_value ? '₦'+(r.contract_value/1e6).toFixed(0)+'M' : '-'}</td><td>${r.category||'-'}</td><td>${r.status?.replace(/_/g,' ')||''}</td><td>${r.deadline ? new Date(r.deadline).toLocaleDateString('en-GB') : '-'}</td></tr>`;
      });
      html += `</tbody></table>`;
    }

    // Signature Activity (FR-076)
    if (signatureLog?.length) {
      html += `<h2>Signature Activity Log</h2>`;
      html += `<table><thead><tr><th>Date</th><th>Officer</th><th>Document</th><th>Applied By</th></tr></thead><tbody>`;
      signatureLog.forEach(s => {
        html += `<tr><td>${new Date(s.timestamp).toLocaleString('en-GB')}</td><td>${s.officer_name||''} (${s.officer_title||''})</td><td>${s.document_name||''}</td><td>${s.applied_by_name||''}</td></tr>`;
      });
      html += `</tbody></table>`;
    }

    // Full Service Request Register (FR-076)
    if (allRequests?.length) {
      html += `<h2>Full Service Request Register</h2>`;
      html += `<table><thead><tr><th>Ref</th><th>Subject</th><th>Type</th><th>Requester</th><th>Department</th><th>Priority</th><th>Status</th><th>Submitted</th></tr></thead><tbody>`;
      allRequests.forEach(r => {
        html += `<tr><td><code>${r.ref||''}</code></td><td>${r.subject||''}</td><td>${r.type?.replace(/_/g,' ')||''}</td><td>${r.requester_name||''}</td><td>${r.requester_dept||'-'}</td><td>${r.priority||''}</td><td>${r.status?.replace(/_/g,' ')||''}</td><td>${r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : ''}</td></tr>`;
      });
      html += `</tbody></table>`;
    }
  }

  // Footer
  html += `<div class="ftr">CONFIDENTIAL · Desicon Engineering Limited · Legal Service Desk · Generated by the Legal Ops Platform v1.0</div>`;

  // Wrap in self-contained HTML document
  const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>DEL Legal Desk · ${titleLabel} · ${dateStr}</title>
<style>
body{font-family:"Segoe UI",Arial,sans-serif;max-width:900px;margin:32px auto;color:#0F172A;padding:0 24px;font-size:13px;line-height:1.5;}
.hdr{border-bottom:3px solid #1046A0;padding-bottom:14px;margin-bottom:22px;}
.hdr-logo{font-size:20px;font-weight:700;color:#0A1F44;}
.logo-icon{display:inline-block;background:#0A1F44;color:#fff;padding:3px 8px;border-radius:4px;font-size:12px;font-weight:700;letter-spacing:1px;margin-right:8px;vertical-align:middle;}
.hdr-sub{font-size:14px;color:#1046A0;margin-top:4px;font-weight:600;}
.hdr-meta{font-size:11px;color:#64748B;margin-top:6px;}
h2{font-size:15px;color:#0A1F44;border-left:4px solid #2563EB;padding-left:10px;margin:26px 0 12px;}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}
.kpi{border:1px solid #DBEAFE;border-radius:8px;padding:14px;background:#F8FAFF;}
.kpi-num{font-size:24px;font-weight:700;color:#0A1F44;}
.kpi-lbl{font-size:11px;color:#64748B;margin-top:4px;}
.kpi-dlt{font-size:10px;margin-top:6px;color:#059669;}
.alerts{list-style:none;padding:0;margin:0;}
.alerts li{background:#FFFBEB;border-left:3px solid #F59E0B;padding:8px 12px;margin-bottom:6px;border-radius:0 6px 6px 0;font-size:12px;}
table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:14px;}
th,td{border:1px solid #DBEAFE;padding:7px 10px;text-align:left;vertical-align:top;}
th{background:#EFF6FF;color:#1046A0;font-size:10.5px;text-transform:uppercase;letter-spacing:.5px;}
tr:nth-child(even) td{background:#F8FAFF;}
code{font-family:Consolas,monospace;font-size:11px;color:#1046A0;}
.compliance-score{font-size:14px;margin-bottom:12px;padding:12px;background:#EFF6FF;border-radius:8px;}
.ftr{margin-top:40px;padding-top:14px;border-top:1px solid #DBEAFE;font-size:10px;color:#64748B;text-align:center;letter-spacing:.5px;}
@media print{body{margin:12px 20px;}.hdr{page-break-after:avoid;}h2{page-break-after:avoid;}table{page-break-inside:auto;}tr{page-break-inside:avoid;}}
</style></head><body>${html}</body></html>`;

  // Trigger download
  const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DEL_Legal_Desk_${isDetailed ? 'Detailed' : 'High-Level'}_${today.toISOString().slice(0,10)}.html`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}
